import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { ConfigService as NgLibConfigService } from '@ngx-config/core';
import { sprintf } from 'sprintf-js';
import { isObject, isString } from 'util';

import * as ConfigActions from '../../state-management/actions/config';

import { Config } from './models/config';
import { ConfigResponse } from './models/config-response';
import { State as AppState, getSelectedAgency, getApiKeys, getUserId, getXenaConfig, getXenaGlobalConfig, getXenaConfigRev, getXenaGlobalConfigRev, getUraniaConfig, getUraniaConfigRev, getSelectedUraniaCfg, getNeoConfig, getNeoConfigRev } from '../../state-management/reducers';
import { State as ConfigState, CONFIG_SCOPE_XENA, CONFIG_SCOPE_XENA_GLOBAL, CONFIG_SCOPE_URANIA, CONFIG_SCOPE_NEO } from '../../state-management/reducers/config';
import { ConfigSaveResponse } from './models/config-save-response';
import { XenaGlobalConfig } from './models/xena-global-config';
import { UraniaConfig } from './models/urania-config';
import { NeoConfig } from './models/neo-config';



@Injectable()
export class ConfigService implements Resolve<ConfigState> {
	private readonly _defaultScope: string = CONFIG_SCOPE_XENA;

	public constructor(
		private _http: HttpClient,
		private _store: Store<AppState>,
		private _config: NgLibConfigService,
	) {}

	public retrieveConfig(scope?: string): Observable<ConfigResponse> {
		if (!scope) {
			scope = this._defaultScope;
		}

		return this._http.get<ConfigResponse>(sprintf(this._config.getSettings('configApi')[scope].endpoint, {cfg: this._getCfg(scope)}), {
			headers: new HttpHeaders({'X-API-KEY': this._getApiKey(scope.indexOf('xena') !== -1 ? 'xena' : scope)})
		});
	}

	public saveConfig(scope: string, config: Config|XenaGlobalConfig|UraniaConfig|NeoConfig): Observable<ConfigSaveResponse> {
		return this._http.post<ConfigSaveResponse>(sprintf(this._config.getSettings('configApi')[scope].endpoint, {cfg: this._getCfg(scope)}), config, {
			headers: new HttpHeaders({'X-API-KEY': this._getApiKey(scope.indexOf('xena') !== -1 ? 'xena' : scope)})
		});
	}

	public saveAgentConfig(scope: string, config: Partial<Config|XenaGlobalConfig|UraniaConfig|NeoConfig>): Observable<ConfigSaveResponse> {
		return this._http.patch<ConfigSaveResponse>(sprintf(this._config.getSettings('configApi')[scope].endpoint, {cfg: this._getAgentCfg(scope)}), config, {
			headers: new HttpHeaders({'X-API-KEY': this._getApiKey(scope.indexOf('xena') !== -1 ? 'xena' : scope)})
		});
	}

	public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ConfigState> {
		let currentXenaConfig: Config = null;
		let currentXenaGlobalConfig: XenaGlobalConfig = null;
		let currentUraniaConfig: UraniaConfig = null;
		let currentNeoConfig: NeoConfig = null;

		let currentXenaConfigRev: string = null;
		let currentXenaGlobalConfigRev: string = null;
		let currentUraniaConfigRev: string = null;
		let currentNeoConfigRev: string = null;

		this._store.select(getXenaConfig).take(1).subscribe(c => currentXenaConfig = c);
		this._store.select(getXenaGlobalConfig).take(1).subscribe(c => currentXenaGlobalConfig = c);
		this._store.select(getUraniaConfig).take(1).subscribe(c => currentUraniaConfig = c);
		this._store.select(getNeoConfig).take(1).subscribe(c => currentNeoConfig = c);

		this._store.select(getXenaConfigRev).take(1).subscribe(r => currentXenaConfigRev = r);
		this._store.select(getXenaGlobalConfigRev).take(1).subscribe(r => currentXenaGlobalConfigRev = r);
		this._store.select(getUraniaConfigRev).take(1).subscribe(r => currentUraniaConfigRev = r);
		this._store.select(getNeoConfigRev).take(1).subscribe(r => currentNeoConfigRev = r);

		if (
			currentXenaConfig
			&& currentXenaGlobalConfig
			&& currentUraniaConfig
			&& currentNeoConfig
		) {
			return Observable.of([
				{ scope: CONFIG_SCOPE_XENA, config: currentXenaConfig, revision: currentXenaConfigRev, tempConfig: currentXenaConfig },
				{ scope: CONFIG_SCOPE_XENA_GLOBAL, config: currentXenaGlobalConfig, revision: currentXenaGlobalConfigRev, tempConfig: currentXenaGlobalConfig },
				{ scope: CONFIG_SCOPE_URANIA, config: currentUraniaConfig, revision: currentUraniaConfigRev, tempConfig: currentUraniaConfig },
				{ scope: CONFIG_SCOPE_NEO, config: currentNeoConfig, revision: currentNeoConfigRev, tempConfig: currentNeoConfig },
			]);
		}

		return Observable.combineLatest(
			this.retrieveConfig(CONFIG_SCOPE_XENA),
			this.retrieveConfig(CONFIG_SCOPE_XENA_GLOBAL),
			this.retrieveConfig(CONFIG_SCOPE_NEO),
			(
				xenaConfig: ConfigResponse,
				xenaGlobalConfig: ConfigResponse,
				neoConfig: ConfigResponse
			) => ([
				{ scope: CONFIG_SCOPE_XENA, config: xenaConfig.config, revision: xenaConfig.revision, tempConfig: currentXenaConfig },
				{ scope: CONFIG_SCOPE_XENA_GLOBAL, config: xenaGlobalConfig.config, revision: xenaGlobalConfig.revision, tempConfig: currentXenaGlobalConfig },
				{ scope: CONFIG_SCOPE_URANIA, config: null, revision: null, tempConfig: null }, // urania get's initialized if the user navigates to urania-admin page
				{ scope: CONFIG_SCOPE_NEO, config: neoConfig.config, revision: neoConfig.revision, tempConfig: currentNeoConfig },
			])
		).do(configState => configState.forEach(s => this._store.dispatch(new ConfigActions.InitConfigState(s.scope, s.config, s.revision))));
	}

	private _getApiKey(scope: string): string {
		let apiKey: string = '';
		let agentId: number = 0;

		this._store.select(getSelectedAgency)
			.take(1)
			.map(agency => agency.agentId)
			.do(id => agentId = id)
			.switchMap(() => this._store.select(getApiKeys).take(1))
			.subscribe(apiKeys => {
				if (isObject(apiKeys) && isObject(apiKeys[scope]) && isString(apiKeys[scope][agentId])) {
					apiKey = apiKeys[scope][agentId];
				}
			});

		return apiKey;
	}

	private _getCfg(scope?: string): string {
		if (isString(scope) && scope === CONFIG_SCOPE_URANIA) {
			return this._getUraniaCfg();
		} else {
			let selectedAgency: number = -1;
			let userId: number = -1;

			this._store.select(getSelectedAgency).take(1).subscribe(agency => selectedAgency = agency.agentId);
			this._store.select(getUserId).take(1).subscribe(id => userId = id);

			return sprintf('%06d%010d', selectedAgency, userId);
		}
	}

	private _getAgentCfg(scope?: string): string {
		if (isString(scope) && scope === CONFIG_SCOPE_URANIA) {
			return this._getUraniaCfg();
		} else {
			let selectedAgency: number = -1;

			this._store.select(getSelectedAgency).take(1).subscribe(agency => selectedAgency = agency.agentId);

			return sprintf('%06d0000000000', selectedAgency);
		}
	}

	private _getUraniaCfg(): string {
		let uraniaCfg: string = '';

		this._store.select(getSelectedUraniaCfg).take(1).subscribe(cfg => uraniaCfg = isObject(cfg) && isString(cfg.cfg) ? cfg.cfg : '');

		return uraniaCfg;
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/services/config/config.service.ts